import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnReorderEvent,
  GridColumnResizeEvent,
  GridFilterChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridNoRecords,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
  GridToolbar
} from '@progress/kendo-react-grid';
import React, { type CSSProperties } from 'react';
import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import CpsLoader from '../common/CpsLoader';
import { ColumnMenu } from './columnMenu/ColumnMenu';
import { CpsToolbar } from './toolbar/CpsToolbar';
import { GridPager } from './pager/GridPager';
import { FilterActionsHandle } from './toolbar/filterActions';
import {
  CPS_FIELD_TYPE,
  CpsFacetValue,
  CpsFieldForView,
  CpsFieldTypeToFilterType,
  CpsSearchQueryRequest,
  DOC_ID_FIELD,
  PERMISSIONS_FIELD
} from '@cs-cms/types';
import { withFilterDescriptor } from './cpsGridHeaderCell';
import { CpsGridDocument } from './cpsGridTypes';
import { ElementDimensions } from '../hooks/useWindowDimensions';
import { DocumentPreviewPanel, useDocumentPreviewContext, useDocumentPreviewDock } from './documentPreview';

interface ColumnChanges {
  widths: Record<string, number>;
  order: string[];
}

export interface CpsGridViewProps {
  gridRef: React.RefObject<HTMLDivElement | null>;
  onContextMenu: (e: React.MouseEvent<HTMLDivElement>) => void;
  isLoading: boolean;
  openDocumentLoading: boolean;
  currentViewLoading: boolean;
  gridKey: string;
  gridDimensions: ElementDimensions;
  noRecordsOverlayStyle: CSSProperties | undefined;
  sort: SortDescriptor[] | undefined;
  sortChange: (descriptors: SortDescriptor[]) => void;
  compositeFilterDescriptor: CompositeFilterDescriptor | undefined;
  setCompositeFilterDescriptor: ((descriptor: CompositeFilterDescriptor) => void) | undefined;
  onColumnReorder: (event: GridColumnReorderEvent) => void;
  onColumnResize: (event: GridColumnResizeEvent) => void;
  queryRequest: CpsSearchQueryRequest | undefined;
  pageSizeValue: number;
  rowsCount: number;
  pageChange: (event: GridPageChangeEvent) => void;
  gridData: CpsGridDocument[];
  select: SelectDescriptor | undefined;
  onSelectionChange: (event: GridSelectionChangeEvent) => void;
  onHeaderSelectionChange: (event: GridHeaderSelectionChangeEvent) => void;
  cellRender: (cellProps: GridCellProps) => React.ReactNode;
  columnChanges: ColumnChanges;
  columns: CpsFieldForView[] | undefined;
  resetColumnWidths: () => void;
  initialFacets: Record<string, CpsFacetValue[]> | undefined;
  facets: Record<string, CpsFacetValue[]> | undefined;
  actionsFacets: CpsFacetValue[];
  onFilterActionsMount: (actions: FilterActionsHandle) => void;
  onRemoveFilter: (fieldToRemove: string, valueToRemove?: string) => void;
  translate: (key: string) => string;
}

const getEmptyGridMessage = (
  queryRequest: CpsSearchQueryRequest | undefined,
  gridDataLength: number,
  rowsCount: number,
  translate: (key: string) => string
): string => {
  if (queryRequest?.searchText) {
    return translate('grid.searchNoResults');
  }

  if (gridDataLength === 0 && rowsCount > 0) {
    return translate('custom.common.emptyGridPendingDeletionMessage');
  }

  return translate('custom.common.emptyGridMessage');
};

export const CpsGridView: React.FC<CpsGridViewProps> = ({
  gridRef,
  onContextMenu,
  isLoading,
  openDocumentLoading,
  currentViewLoading,
  gridKey,
  gridDimensions,
  noRecordsOverlayStyle,
  sort,
  sortChange,
  compositeFilterDescriptor,
  setCompositeFilterDescriptor,
  onColumnReorder,
  onColumnResize,
  queryRequest,
  pageSizeValue,
  rowsCount,
  pageChange,
  gridData,
  select,
  onSelectionChange,
  onHeaderSelectionChange,
  cellRender,
  columnChanges,
  columns,
  resetColumnWidths,
  initialFacets,
  facets,
  actionsFacets,
  onFilterActionsMount,
  onRemoveFilter,
  translate
}) => {
  const emptyGridMessage = getEmptyGridMessage(queryRequest, gridData.length, rowsCount, translate);
  const { previewDocument } = useDocumentPreviewContext();
  const previewFrame = useDocumentPreviewDock(gridRef, previewDocument !== undefined);
  const containerClassName = previewDocument ? 'cpsGridContainer cpsGridContainerWithPreview' : 'cpsGridContainer';

  return (
    <div ref={gridRef} className={containerClassName} onContextMenu={onContextMenu}>
      {(isLoading || openDocumentLoading || currentViewLoading) && (
        <CpsLoader
          type={openDocumentLoading ? 'pulsing' : 'infinite-spinner'}
          textMessageKey={openDocumentLoading ? 'custom.common.loader.opening' : undefined}
        />
      )}
      <Tooltip openDelay={500} anchorElement="target" parentTitle={true} position="bottom">
        <Grid
          key={gridKey}
          className="cpsGrid"
          style={{ height: gridDimensions.height }}
          sortable={true}
          sort={sort}
          onSortChange={(event: GridSortChangeEvent) => sortChange(event.sort)}
          onFilterChange={(event: GridFilterChangeEvent) => {
            if (setCompositeFilterDescriptor) {
              setCompositeFilterDescriptor(event.filter);
            }
          }}
          reorderable={true}
          onColumnReorder={onColumnReorder}
          onColumnResize={onColumnResize}
          pageable={true}
          scrollable="scrollable"
          skip={queryRequest?.skip}
          take={pageSizeValue}
          total={rowsCount}
          onPageChange={pageChange}
          data={gridData}
          dataItemKey={DOC_ID_FIELD}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: 'multiple'
          }}
          select={select}
          onSelectionChange={onSelectionChange}
          onHeaderSelectionChange={onHeaderSelectionChange}
          navigatable={true}
          resizable={true}
          size={'small'}
          pager={pagerProps => (
            <GridPager
              total={pagerProps.total}
              skip={pagerProps.skip}
              take={pagerProps.take}
              buttonCount={pagerProps.buttonCount}
              info={pagerProps.info}
              type={pagerProps.type}
              pageSizes={pagerProps.pageSizes}
              previousNext={pagerProps.previousNext}
              responsive={pagerProps.responsive}
              onPageChange={pagerProps.onPageChange}
              columnChanges={columnChanges}
              columns={columns}
              onSaveComplete={resetColumnWidths}
            />
          )}
          cells={{ data: cellRender, headerCell: withFilterDescriptor(compositeFilterDescriptor) }}
        >
          <GridNoRecords>{emptyGridMessage}</GridNoRecords>
          <GridToolbar className="cpsToolbarContainer" nextButton={() => null} prevButton={() => null} overflow="none">
            <CpsToolbar
              onFilterActionsMount={onFilterActionsMount}
              initialFacets={initialFacets}
              compositeFilterDescriptor={compositeFilterDescriptor}
              columns={columns}
              onRemoveFilter={onRemoveFilter}
            />
          </GridToolbar>
          <GridColumn columnType="checkbox" reorderable={false} resizable={false} />
          {columnChanges.order
            .map(fieldName => columns?.find(col => col.name === fieldName))
            .filter((column): column is NonNullable<typeof column> => Boolean(column))
            .map(column => (
              <GridColumn
                key={column.id}
                field={column.name}
                title={column.displayName}
                sortable={column.sortable}
                filter={column.name === PERMISSIONS_FIELD ? undefined : CpsFieldTypeToFilterType(column.type)}
                width={columnChanges.widths[column.name] ?? column.width}
                columnMenu={columnMenuProps => {
                  const fieldFacets = column.name === PERMISSIONS_FIELD ? actionsFacets : facets?.[column.name];

                  return (
                    <ColumnMenu
                      column={columnMenuProps.column}
                      columnsState={columnMenuProps.columnsState}
                      onColumnsStateChange={columnMenuProps.onColumnsStateChange}
                      sort={columnMenuProps.sort}
                      onFilterChange={columnMenuProps.onFilterChange}
                      onCloseMenu={columnMenuProps.onCloseMenu}
                      filterOperators={columnMenuProps.filterOperators}
                      onSortChange={sortChange}
                      sortable={column.sortable}
                      filterable={column.filterable}
                      filter={compositeFilterDescriptor}
                      field={column.name}
                      fieldFacets={fieldFacets}
                      isNumericColumn={CpsFieldTypeToFilterType(column.type) === CPS_FIELD_TYPE.NUMERIC}
                      supportsBulkFilter={
                        column.type === CPS_FIELD_TYPE.TEXT && (!fieldFacets || fieldFacets.length === 0)
                      }
                    />
                  );
                }}
              />
            ))}
        </Grid>
      </Tooltip>
      {gridData.length === 0 && noRecordsOverlayStyle && (
        <div className="cpsGridNoRecordsOverlay" style={noRecordsOverlayStyle}>
          {emptyGridMessage}
        </div>
      )}
      {previewDocument && previewFrame ? <DocumentPreviewPanel frame={previewFrame} /> : null}
    </div>
  );
};
